/* eslint-disable max-len */

import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import { client } from '../../../../../common-utils/libraries/axios/axios';
import subscriptionFields from '../../fields/subscriptionFields';
import { SUBSCRIPTIONS } from '../apiUrls';
import { buildUrl } from '@/common-utils/functions/restUtils';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';

/**
 * @param responseData response.data object
 * @param responseData.uuid string
 * @param responseData.email string
 * @param responseData.label string
 * @param responseData.provider string
 * @param responseData.status string
 * @param responseData.createdAt string
 * @param responseData.updatedAt string
 * @param response
 * @param responseData.items
 * @param responseData.totalResults
 * @returns Mapped data object
 */
function mapResponseData({ items, totalResults }) {
    const data = {};

    const mappedItems = items.map(item => {
        return {
            [subscriptionFields.UUID.key]: item[subscriptionFields.UUID.key],
            [subscriptionFields.PROVIDER.key]: item[subscriptionFields.PROVIDER.key],
            [subscriptionFields.ACTIVATION_DATE.key]: item[subscriptionFields.ACTIVATION_DATE.key],
            [subscriptionFields.START_BILLING_DATE.key]: item[subscriptionFields.START_BILLING_DATE.key],
            [subscriptionFields.IS_MIGRATING.key]: item[subscriptionFields.IS_MIGRATING.key],
            [subscriptionFields.LAST_CONNECTION_DATE.key]: item[subscriptionFields.LAST_CONNECTION_DATE.key],
            [subscriptionFields.LAST_CONNECTION_TYPE.key]: item[subscriptionFields.LAST_CONNECTION_TYPE.key],
            [subscriptionFields.LAST_SOURCE_IP.key]: item[subscriptionFields.LAST_SOURCE_IP.key],
            [subscriptionFields.LAST_ICCID.key]: item[subscriptionFields.LAST_ICCID.key],
            [subscriptionFields.LAST_ICCID_OPERATOR.key]: item[subscriptionFields.LAST_ICCID_OPERATOR.key],
            [subscriptionFields.ACTIVE_ICCID.key]: item[subscriptionFields.ACTIVE_ICCID.key],
            [subscriptionFields.ACTIVE_ICCID_OPERATOR.key]: item[subscriptionFields.ACTIVE_ICCID_OPERATOR.key],
            [subscriptionFields.LAST_SSL_VERSION.key]: item[subscriptionFields.LAST_SSL_VERSION.key],
            [subscriptionFields.STATUS.key]: item[subscriptionFields.STATUS.key],
            [subscriptionFields.CREATED_AT.key]: item[subscriptionFields.CREATED_AT.key],
            [subscriptionFields.UPDATED_AT.key]: item[subscriptionFields.UPDATED_AT.key],
            [subscriptionFields.RESILIATION_DATE.key]: item[subscriptionFields.RESILIATION_DATE.key],
            [subscriptionFields.MERCHANT_ORGANIZATION.key]: item[subscriptionFields.MERCHANT_ORGANIZATION.key],
            [subscriptionFields.CLIENT.key]: {
                [subscriptionFields.CLIENT.UUID.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.CLIENT.UUID)),
                [subscriptionFields.CLIENT.LEGAL_NAME.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.CLIENT.LEGAL_NAME)),
                [subscriptionFields.CLIENT.CLIENT_CODE.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.CLIENT.CLIENT_CODE))
            },
            [subscriptionFields.POS_TERMINAL.key]: {
                [subscriptionFields.POS_TERMINAL.UUID.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.UUID)),
                [subscriptionFields.POS_TERMINAL.SERIAL_NUMBER.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SERIAL_NUMBER)),
                [subscriptionFields.POS_TERMINAL.MANUFACTURER.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.MANUFACTURER)),
                [subscriptionFields.POS_TERMINAL.LABEL.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.LABEL)),
                [subscriptionFields.POS_TERMINAL.CUSTOM_LABEL.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.CUSTOM_LABEL)),
                [subscriptionFields.POS_TERMINAL.STATUS.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.STATUS)),
                [subscriptionFields.POS_TERMINAL.BLACKLIST_START_DATE.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.BLACKLIST_START_DATE)),
                [subscriptionFields.POS_TERMINAL.REWAREHOUSE_DATE.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.REWAREHOUSE_DATE)),
                [subscriptionFields.POS_TERMINAL.LAST_CONNECTION_GPRS.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.LAST_CONNECTION_GPRS)),
                [subscriptionFields.POS_TERMINAL.LAST_CONNECTION_IP.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.LAST_CONNECTION_IP)),
                [subscriptionFields.POS_TERMINAL.HAS_MIGRATED.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.HAS_MIGRATED)),
                [subscriptionFields.POS_TERMINAL.IS_BROADCAST.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.IS_BROADCAST)),
                [subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.key]: {
                    [subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.PROVIDER_UUID.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.PROVIDER_UUID)),
                    [subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.PROVIDER_LEGAL_NAME.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.DIRECT_PROVIDER.PROVIDER_LEGAL_NAME))
                },
                [subscriptionFields.POS_TERMINAL.SIM.key]: {
                    [subscriptionFields.POS_TERMINAL.SIM.SIM_ACTIVATED_ON.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SIM.SIM_ACTIVATED_ON)),
                    [subscriptionFields.POS_TERMINAL.SIM.SIM_STATUS.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SIM.SIM_STATUS)),
                    [subscriptionFields.POS_TERMINAL.SIM.SIM_OFFER.key]: {
                        [subscriptionFields.POS_TERMINAL.SIM.SIM_OFFER.SHORT_DISPLAY_NAME.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SIM.SIM_OFFER.SHORT_DISPLAY_NAME))
                    }
                }
            },
            [subscriptionFields.SUBSCRIPTION_PLAN.key]: {
                [subscriptionFields.SUBSCRIPTION_PLAN.LABEL.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.SUBSCRIPTION_PLAN.LABEL)),
                [subscriptionFields.SUBSCRIPTION_PLAN.PRODUCT_CODE.key]: getObjectAttributeFromStringKey(item, generateNestedFieldAccessor(subscriptionFields.SUBSCRIPTION_PLAN.PRODUCT_CODE))
            }
        };
    });

    data.items = mappedItems;
    data.totalResults = totalResults;
    return data;
}

const queryGetSubscriptions = (query, enabled) => {
    return {
        queryKey: [SUBSCRIPTIONS.COLLECTION.queryKey, query],
        queryFn: async () => {
            const response = await client.get(buildUrl(SUBSCRIPTIONS.COLLECTION.url, query));
            return mapResponseData(response.data);
        },
        enabled: enabled
    };
};


export default queryGetSubscriptions;